import { Pagination } from '@/constants/pagination';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type FunctionsPagination = {
  pageNumber: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
};

export type FunctionsFilter = {
  roleId: string;
  roleCode: string;
  departmentCode: string;
  pageNumber: number;
  pageSize: number;
  search?: string;
};

export type FunctionsState = {
  dataFunctionsByRoleAndDepartment: FunctionNodeType[];
  dataAllFunctions: FunctionsType[];
  dataActionFunction: FunctionsActionsType[];
  pagination: PaginationAPI;
  paginationFunction: FunctionsPagination;
  filter: FunctionsFilter;
  totalElements?: number;
  functionClaims: FunctionClaimsResponse | null;
  functionChildren: FunctionNodeType[];
};

export const functionsInitialState: FunctionsState = {
  dataFunctionsByRoleAndDepartment: [],
  dataAllFunctions: [],
  dataActionFunction: [],
  paginationFunction: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: Pagination.pageSize,
  },
  pagination: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: Pagination.pageSize,
  },
  filter: {
    roleId: '',
    roleCode: 'ADMIN',
    departmentCode: '',
    pageNumber: Pagination.pageNumber,
    pageSize: Pagination.pageSize,
    search: '',
  },
  functionClaims: null,
  functionChildren: [],
};

const slice = createSlice({
  name: 'functions',
  initialState: functionsInitialState,
  reducers: {
    setListFunctionsByRoleAndDepartmentAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: FunctionNodeType[];
        pagination?: FunctionsPagination;
      }>,
    ) {
      state.dataFunctionsByRoleAndDepartment = payload.data;
      if (payload.pagination) {
        state.paginationFunction = payload.pagination;
      }
    },
    setFunctionsFilterAction(state, { payload }: PayloadAction<Partial<FunctionsFilter>>) {
      state.filter = { ...state.filter, ...payload };
    },
    setListFunctionsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: FunctionsType[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.dataAllFunctions = payload.data;
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },
    setActionsFunctionAction(
      state,
      { payload }: PayloadAction<{ dataActionFunction: FunctionsActionsType[] }>,
    ) {
      state.dataActionFunction = payload.dataActionFunction;
    },

    deleteFunctionsAction(state, { payload }: PayloadAction<string>) {
      state.dataAllFunctions = state.dataAllFunctions.filter((item) => !payload.includes(item.id!));
    },
    addFunctionsAction(state, { payload }: PayloadAction<FunctionsType>) {
      state.dataAllFunctions = [payload, ...state.dataAllFunctions];
      state.pagination = {
        ...state.pagination,
        pageSize: state.pagination.pageSize + 1,
        totalElements: state.pagination.totalElements + 1,
      };
    },
    addChildFunctionAction(state, { payload }: PayloadAction<FunctionsChildren>) {
      state.dataAllFunctions = state.dataAllFunctions.map((parent) => {
        if (parent.id !== payload.parentId) return parent;
        return {
          ...parent,
          children: [...(parent.children ?? []), payload],
        };
      });
    },
    updateFunctionAction(state, { payload }: PayloadAction<FunctionsType>) {
      state.dataAllFunctions = state.dataAllFunctions.map((item) => {
        if (item?.id === payload?.id) {
          return payload;
        }
        return item;
      });
    },
    updateFunctionChildAction(state, { payload }: PayloadAction<FunctionsChildren>) {
      state.dataAllFunctions = state.dataAllFunctions.map((parent) => {
        const hasChild = parent.children?.some((child) => child.id === payload.id);
        if (!hasChild) return parent;
        return {
          ...parent,
          children: parent.children.map((child) =>
            child.id === payload.id ? payload : child,
          ),
        };
      });
    },

    setFunctionClaimsAction(state, action: PayloadAction<FunctionClaimsResponse | null>) {
      state.functionClaims = action.payload;
    },

    clearFunctionClaimsAction(state) {
      state.functionClaims = null;
    },

    setFunctionChildrenAction(state, action: PayloadAction<FunctionNodeType[]>) {
      state.functionChildren = action.payload;
    },

    clearFunctionChildrenAction(state) {
      state.functionChildren = [];
    },
  },
});

export const {
  setListFunctionsByRoleAndDepartmentAction,
  setFunctionsFilterAction,
  setListFunctionsAction,
  setActionsFunctionAction,
  deleteFunctionsAction,
  addFunctionsAction,
  addChildFunctionAction,
  updateFunctionAction,
  updateFunctionChildAction,
  setFunctionClaimsAction,
  clearFunctionClaimsAction,
  setFunctionChildrenAction,
  clearFunctionChildrenAction,
} = slice.actions;
export default slice.reducer;
